'use client'

import { Container } from 'components/Container'
import Image from 'next/image'
import { useCallback, useEffect, useState } from 'react'
import mobileMap from 'public/illustrations/kontakt-map-mobile.png'
import desktopMap from 'public/illustrations/kontakt-map-desktop.png'
import { trackGoal } from 'lib/analytics'
import { ControlledSelectInput } from 'components/input/ControlledSelectInput'
import { useTranslations } from 'next-intl'
import { Button } from 'components/Button'

const isIOS =
  typeof window !== 'undefined' &&
  /(iPad|iPhone|iPod)/gi.test(navigator.userAgent)

const isAndroid =
  typeof window !== 'undefined' && /Android/gi.test(navigator.userAgent)

type MapProvider = 'osm' | 'gmaps' | 'ddg' | 'bing' | 'apple' | 'geo' | string

type MapLink = {
  href: string
  label: string
}

type MapLinks = {
  [key in MapProvider]: MapLink
}

const mapLinks: MapLinks = {
  osm: {
    href: 'https://osm.org/go/0HoGbdTQP?way=23227564',
    label: 'OpenStreetMap',
  },
  gmaps: {
    href: 'https://goo.gl/maps/9Xibka8M3hnAncyX9',
    label: 'Google Maps',
  },
  ddg: {
    href: 'https://duckduckgo.com/?q=farbenmeer+GmbH+Hamburg+Germany&iaxm=maps',
    label: 'DuckDuckGo',
  },
  bing: {
    href: 'https://www.bing.com/maps?osid=b4ea4ab3-d6c1-4b27-9ceb-38522730ce0f&cp=53.55785%7E9.968956&lvl=16.0',
    label: 'Bing Maps',
  },
  apple: {
    href: 'https://maps.apple.com/place?auid=16848566498952545990',
    label: 'Apple Maps',
  },
}

const mapLinksWithAndroid: MapLinks = {
  geo: {
    href: 'geo:53.55781459541504,9.967721700668335?q=farbenmeer+GmbH+Hamburg+Germany',
    label: 'Native App',
  },
  ...mapLinks,
}

const defaultOption: MapLink = mapLinks['osm']!

export function MapBlock() {
  const t = useTranslations()
  const [options, setOptions] = useState(mapLinks)
  const [selected, setSelected] = useState<MapLink>(defaultOption)
  const [linkTarget, setLinkTarget] = useState('_blank')

  // Find the option for the given href
  const getMapLinkByHref = useCallback(
    (href: string) =>
      Object.values(options).find((o) => o.href === href) ?? defaultOption,
    [options],
  )

  // Set default selection for mobile devices on mount
  useEffect(() => {
    if (!isIOS && !isAndroid) return

    // Use setTimeout to avoid synchronous setState in effect
    setTimeout(() => {
      setLinkTarget('_self')

      if (isAndroid) {
        setOptions(mapLinksWithAndroid)
        setSelected(mapLinksWithAndroid['geo']!)
      } else {
        setSelected(mapLinks['apple']!)
      }
    }, 0)
  }, [])

  return (
    <section className="bg-cover text-white">
      <Container className="flex flex-col justify-end !p-0 relative">
        <div className="-z-10 inset-0 md:hidden">
          <Image src={mobileMap} alt={t('map.alt')} sizes="100vw" />
        </div>
        <div className="-z-10 inset-0 hidden md:block">
          <Image src={desktopMap} alt={t('map.alt')} />
        </div>
        <div className="flex flex-row w-full justify-end absolute py-32 m-auto px-24 lg:px-64 xl:px-96">
          <ControlledSelectInput
            title={t('map.chooseProvider')}
            className="mr-32 md:w-256 text-12"
            options={Object.entries(options).map(([, v]) => [v.href, v.label])}
            onChange={(href) => setSelected(getMapLinkByHref(href))}
            value={selected.href}
          />
          <div className="max-w-128 md:max-w-192 ">
            <a
              href={selected.href}
              target={linkTarget}
              rel="noopener noreferrer"
              className="h-full"
              onClick={() =>
                trackGoal('click:map', {
                  props: { mapProvider: selected.label },
                })
              }
              aria-label={t('map.openWith', { provider: selected.label })}
            >
              <Button
                arrow="md"
                slim
                className="bg-white text-black border border-white"
              >
                {t('map.open')}
              </Button>
            </a>
          </div>
        </div>
      </Container>
    </section>
  )
}
